import AutoModeIcon from '@mui/icons-material/AutoMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import Box from '@mui/material/Box';
import Button, { ButtonProps } from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { PopoverOrigin } from '@mui/material/Popover';
import Slide from '@mui/material/Slide';
import { useColorScheme } from '@mui/material/styles';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';
import AppContext from './AppContext';

export interface ThemeSwitcherProps {
  size?: ButtonProps['size'];
  animate?: boolean;
  direction?: 'horizontal' | 'vertical';
  menu?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  sx?: ButtonProps['sx'];
}

export default function ThemeSwitcher({
  anchorOrigin = { horizontal: 'right', vertical: 'bottom' },
  animate = true,
  direction = 'vertical',
  menu,
  size,
  transformOrigin = { horizontal: 'right', vertical: 'top' },
  sx,
}: ThemeSwitcherProps) {
  const app = useContext(AppContext);
  const { t } = useTranslation(app.i18n.lng);
  const containerRef = useRef<HTMLButtonElement>(null);
  const { mode, setMode, colorScheme } = useColorScheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const lightModeIcon = <LightModeIcon />;
  const darkModeIcon = <DarkModeIcon />;
  const colorSchemeIcon =
    colorScheme === 'light' ? lightModeIcon : darkModeIcon;
  const modeIcon = mode === 'system' ? <AutoModeIcon /> : colorSchemeIcon;

  return (
    <TransitionGroup>
      {!mode ? null : !menu ? (
        <Grow>
          <IconButton
            size={size}
            sx={sx}
            onClick={() =>
              setMode(
                mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light'
              )
            }
          >
            {modeIcon}
          </IconButton>
        </Grow>
      ) : (
        <Grow>
          <Box>
            <Button
              id="theme-switcher-button"
              aria-controls={open ? 'theme-switcher-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              disableElevation
              size={size}
              variant="outlined"
              sx={{
                borderRadius: 2,
                color: 'text.secondary',
                backgroundColor: 'transparent',
                borderColor: 'divider',
                minWidth: 0,
                width: 'auto',
                height: 'auto',
                overflow: 'hidden',
                ...sx,
              }}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                setAnchorEl(event.currentTarget);
              }}
            >
              {animate ? (
                <>
                  <Box
                    sx={{
                      position: 'relative',
                      // Match the size of the icon
                      width: 24,
                      height: 24,
                      fontSize: '1.5rem',
                    }}
                    ref={containerRef}
                  >
                    <Slide
                      in={colorScheme === 'light'}
                      container={containerRef.current}
                      direction={direction === 'vertical' ? 'down' : 'right'}
                    >
                      <LightModeIcon
                        sx={{
                          position: 'absolute',
                          inset: 0,
                        }}
                      />
                    </Slide>
                    <Slide
                      in={colorScheme === 'dark'}
                      container={containerRef.current}
                      direction={direction === 'vertical' ? 'up' : 'left'}
                    >
                      <DarkModeIcon
                        sx={{
                          position: 'absolute',
                          inset: 0,
                        }}
                      />
                    </Slide>
                  </Box>
                </>
              ) : (
                colorSchemeIcon
              )}
            </Button>
            <Menu
              id="theme-switcher-menu"
              anchorEl={anchorEl}
              anchorOrigin={anchorOrigin}
              transformOrigin={transformOrigin}
              open={open}
              onClose={() => {
                setAnchorEl(null);
              }}
              slotProps={{
                list: {
                  'aria-labelledby': 'theme-switcher-button',
                },
              }}
            >
              <MenuItem
                dense
                selected={mode === 'system'}
                onClick={() => {
                  setMode('system');
                  setAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  <AutoModeIcon />
                </ListItemIcon>
                <ListItemText>
                  {t('theme.switcher.system', 'System')}
                </ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                dense
                selected={mode === 'light'}
                onClick={() => {
                  setMode('light');
                  setAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  <LightModeIcon />
                </ListItemIcon>
                <ListItemText>
                  {t('theme.switcher.light', 'Light')}
                </ListItemText>
              </MenuItem>
              <MenuItem
                dense
                selected={mode === 'dark'}
                onClick={() => {
                  setMode('dark');
                  setAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  <DarkModeIcon />
                </ListItemIcon>
                <ListItemText>{t('theme.switcher.dark', 'Dark')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Grow>
      )}
    </TransitionGroup>
  );
}
